import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { safeFormatDate, toIsoLocal } from '../lib/dateTime';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Palmtree,
  CalendarDays,
  CalendarRange,
  CalendarCheck,
  AlertCircle,
  ArrowDownRight,
  Banknote,
  Edit3,
  ArrowLeft,
  History,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { UserSelect } from '../components/UserSelect';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { formatHoursToHM } from '../lib/formatTime';

// Extracted components to prevent re-renders (React best practice)
const PeriodCard = ({ title, icon: Icon, period, color, formatPeriod, getBalanceColor, getBalanceIcon }) => {
  const balance = period?.balance || 0;
  return (
    <div className="card-glass p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            color === 'indigo' ? 'bg-indigo-500/20 text-indigo-400' :
            color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
            'bg-lime-500/20 text-lime-400'
          }`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-foreground">{title}</h3>
            <p className="text-xs text-muted-foreground">
              {period?.period_start && period?.period_end 
                ? formatPeriod(period.period_start, period.period_end)
                : '-'}
            </p>
          </div>
        </div>
        {getBalanceIcon(balance)}
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">Soll</p>
          <p className="text-base font-mono font-semibold text-foreground">
            {formatHoursToHM(period?.target_hours || 0)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Ist</p>
          <p className="text-base font-mono font-semibold text-indigo-600 dark:text-indigo-400">
            {formatHoursToHM(period?.actual_hours || 0)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Saldo</p>
          <p className={`text-base font-mono font-semibold ${getBalanceColor(balance)}`}>
            {formatHoursToHM(balance || 0, true)}
          </p>
        </div>
      </div>
    </div>
  );
};

const VacationStatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div className="card-glass p-5">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
        color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
        color === 'lime' ? 'bg-lime-500/20 text-lime-400' :
        'bg-indigo-500/20 text-indigo-400'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <p className="text-2xl font-heading font-bold text-foreground mb-1">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
    {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
  </div>
);

export default function TimeAccountPage() {
  const { user, api } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Initialize selectedUser from query parameter - on team page, user is required
  const userQueryParam = searchParams.get('user');
  
  const [activeTab, setActiveTab] = useState('overview'); // kept for potential future use
  const [timeAccount, setTimeAccount] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedUser, setSelectedUser] = useState(() => {
    // Initialize from query param if present
    return userQueryParam || '';
  });
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Overtime state
  const [overtimeBalance, setOvertimeBalance] = useState(null);
  const [overtimeLedger, setOvertimeLedger] = useState([]);

  // Correction dialog state (Admin/Accounting only)
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [correctionHours, setCorrectionHours] = useState('');
  const [correctionDate, setCorrectionDate] = useState(toIsoLocal(new Date()));
  const [correctionNote, setCorrectionNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [weeksData, setWeeksData] = useState(null);
  const [weeksLoading, setWeeksLoading] = useState(false);
  const [onlyWithEntries, setOnlyWithEntries] = useState(true);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [weekDetails, setWeekDetails] = useState({});
  const [weekDetailLoading, setWeekDetailLoading] = useState(null);

  useEffect(() => {
    if (!selectedUser) { setSummary(null); return; }
    api.get(`/time-account/me/summary?year=${selectedYear}&user_id=${selectedUser}`)
      .then((res) => setSummary(res.data))
      .catch(() => setSummary(null));
  }, [api, selectedUser, selectedYear]);

  useEffect(() => {
    setWeeksData(null);
    setExpandedWeek(null);
    setWeekDetails({});
  }, [selectedUser, selectedYear]);

  useEffect(() => {
    if (activeTab !== 'weeks' || !selectedUser || weeksData) return;
    setWeeksLoading(true);
    api.get(`/time-account/me/weeks?year=${selectedYear}&user_id=${selectedUser}`)
      .then((res) => setWeeksData(res.data))
      .catch(() => toast.error('Wochenübersicht konnte nicht geladen werden'))
      .finally(() => setWeeksLoading(false));
  }, [api, activeTab, selectedUser, selectedYear, weeksData]);

  const toggleWeek = async (week) => {
    if (expandedWeek === week.week_start) { setExpandedWeek(null); return; }
    setExpandedWeek(week.week_start);
    if (weekDetails[week.week_start]) return;
    setWeekDetailLoading(week.week_start);
    try {
      const res = await api.get(`/time-entries/week/${week.week_start}?user_id=${selectedUser}`);
      setWeekDetails((prev) => ({ ...prev, [week.week_start]: res.data }));
    } catch {
      toast.error('Wochendetails konnten nicht geladen werden');
    } finally {
      setWeekDetailLoading(null);
    }
  };

  const fmtH = (v) => formatHoursToHM(Number(v || 0));
  const fmtSigned = (v) => formatHoursToHM(Number(v || 0), true);
  const deltaColor = (v) => (v > 0 ? 'text-emerald-600 dark:text-emerald-400' : v < 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground');
  const STATUS_BADGES = {
    approved: ['genehmigt', 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'],
    pending: ['eingereicht', 'bg-blue-500/15 text-blue-700 dark:text-blue-300'],
    rejected: ['abgelehnt', 'bg-amber-500/15 text-amber-700 dark:text-amber-300'],
    fixed_by_supervisor: ['festgesetzt', 'bg-violet-500/15 text-violet-700 dark:text-violet-300'],
    final: ['final', 'bg-muted text-muted-foreground'],
    withdrawn: ['zurückgezogen', 'bg-muted text-muted-foreground'],
  };
  const LEAVE_LABELS = { vacation: 'Urlaub', sick: 'Krank', special: 'Sonderurlaub', special_leave: 'Sonderurlaub', overtime_reduction: 'Zeitausgleich' };
  const todayIso = toIsoLocal(new Date());

  const visibleWeeks = (weeksData?.weeks || [])
    .filter((w) => w.in_scope !== false && w.week_start <= todayIso)
    .filter((w) => !onlyWithEntries || w.has_entries || w.delta_hours !== 0)
    .slice()
    .reverse();

  const renderWeekDetail = (week) => {
    const detail = weekDetails[week.week_start];
    if (weekDetailLoading === week.week_start || !detail) {
      return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
    }
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(week.week_start + 'T00:00:00');
      d.setDate(d.getDate() + i);
      return toIsoLocal(d);
    });
    const accountStart = weeksData?.account_start || '';
    const isFirstWeek = !!accountStart && week.week_start <= accountStart && accountStart <= week.week_end;
    const openingLabel = !isFirstWeek
      ? `Kontostand Ende KW ${week.iso_week - 1}`
      : accountStart === `${selectedYear}-01-01`
        ? 'Übertrag aus dem Vorjahr'
        : 'Anfangsbestand zum Kontostart';
    const dayLabel = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' });
    const leaveFor = (iso) => (detail.vacations || []).find((v) => v.start_date <= iso && iso <= v.end_date);
    const sumHours = (list) => list.reduce((acc, e) => acc + Number(e.hours || 0), 0);
    const weekWork = sumHours(detail.work_time || []);
    const weekProject = sumHours(detail.project_time || []);
    return (
      <div className="border-t border-border p-4 space-y-3 bg-accent/10" data-testid={`week-detail-${week.week_start}`}>
        <p className="text-sm text-muted-foreground">
                   {openingLabel}: <span className="font-mono text-foreground">{fmtH(week.opening_balance)}</span>
        </p>
        <div>
          {days.map((iso, dayIdx) => {
            const holiday = detail.holidays?.[iso];
            const leave = leaveFor(iso);
            const wt = (detail.work_time || []).filter((e) => e.date === iso);
            const pt = (detail.project_time || []).filter((e) => e.date === iso);
            const isEmpty = !holiday && !leave && wt.length === 0 && pt.length === 0;
            const sep = dayIdx > 0 ? 'border-t border-border' : '';
            if (isEmpty) {
              return (
                <div key={iso} className={`grid grid-cols-[110px_1fr] gap-3 text-sm py-1 text-muted-foreground ${sep}`}>
                  <div className="font-semibold">{dayLabel(iso)}</div>
                  <div>–</div>
                </div>
              );
            }
            return (
              <div key={iso} className={`grid grid-cols-[110px_1fr] gap-3 text-sm py-2 ${sep} ${holiday ? 'text-muted-foreground' : ''}`}>
                <div className="font-semibold">{dayLabel(iso)}</div>
                <div className="space-y-2">
                  {(holiday || leave) && (
                    <div className="flex flex-wrap gap-1">
                      {holiday && <span className="px-1.5 py-0.5 rounded text-[10px] bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">{holiday}</span>}
                      {leave && <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-700 dark:text-amber-300">{LEAVE_LABELS[leave.leave_type] || leave.leave_type}</span>}
                    </div>
                  )}
                  {wt.length > 0 && (
                    <div className="border-l-2 border-indigo-500/60 pl-3 space-y-1">
                      <div className="flex justify-between gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <span>Arbeitszeit (Anwesenheit)</span>
                        <span className="font-mono">{fmtH(sumHours(wt))}</span>
                      </div>
                      {wt.map((e) => (
                        <div key={e.id} className="flex justify-between gap-2">
                          <div>
                            <span className="text-foreground">{e.start_time && e.end_time ? `${e.start_time} - ${e.end_time}` : ''}</span>
                            {e.start_time && e.end_time && <span className="text-muted-foreground ml-2">Pause {e.break_minutes || 0} min</span>}
                            {e.note && <p className="text-xs text-muted-foreground">{e.note}</p>}
                          </div>
                          <span className="font-mono text-foreground">{fmtH(e.hours)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {pt.length > 0 && (
                    <div className="border-l-2 border-teal-500/60 pl-3 space-y-1">
                      <div className="flex justify-between gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <span>Projektzeit (Buchung)</span>
                        <span className="font-mono">{fmtH(sumHours(pt))}</span>
                      </div>
                      {pt.map((e) => (
                        <div key={e.id} className="flex justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground">{e.project_name || 'Projekt'}</p>
                            {e.customer_name && <p className="text-xs text-muted-foreground pl-2">{e.customer_name}</p>}
                            {e.description && <p className="text-xs text-muted-foreground pl-2">{e.description}</p>}
                          </div>
                          <span className="font-mono text-foreground">{fmtH(e.hours)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border text-sm">
          <span className={`font-medium ${deltaColor(week.delta_hours)}`}>Veränderung: <span className="font-mono">{fmtSigned(week.delta_hours)}</span></span>
          <span className="text-muted-foreground">
            Arbeitszeit <span className="font-mono">{fmtH(weekWork)}</span> · Projektzeit <span className="font-mono">{fmtH(weekProject)}</span>
            {week.ledger_hours !== 0 && <> · Korrekturen <span className="font-mono">{fmtSigned(week.ledger_hours)}</span></>}
          </span>
          <span className="text-muted-foreground">Kontostand Ende KW {week.iso_week}: <span className={`font-mono ${deltaColor(week.closing_balance)}`}>{fmtH(week.closing_balance)}</span></span>
        </div>
      </div>
    );
  };

  const canCorrect = user?.role === 'admin' || user?.role === 'accounting';
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [api]);

  const fetchData = useCallback(async () => {
    // Only fetch if user is selected
    if (!selectedUser) {
      setTimeAccount(null);
      setOvertimeLedger([]);
      setOvertimeBalance(null);
      return;
    }
    
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear.toString());
      params.append('user_id', selectedUser);

      const [accountRes, overtimeRes] = await Promise.all([
        api.get(`/time-account?${params.toString()}`),
        api.get(`/overtime/ledger?${params.toString()}`),
      ]);
      
      setTimeAccount(accountRes.data);
      setOvertimeLedger(overtimeRes.data.entries || []);
      setOvertimeBalance(overtimeRes.data.balance || null);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für diesen Mitarbeiter');
      } else {
        toast.error('Fehler beim Laden der Daten');
      }
    } finally {
      setIsLoading(false);
    }
  }, [api, selectedYear, selectedUser]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitCorrection = async () => {
    if (!correctionHours || correctionHours === '' || correctionHours === '0') {
      toast.error('Bitte geben Sie Stunden ein (positiv oder negativ)');
      return;
    }

    if (!correctionNote.trim()) {
      toast.error('Begründung ist erforderlich');
      return;
    }

    if (!correctionDate) {
      toast.error('Bitte wählen Sie ein Datum');
      return;
    }

    if (!selectedUser) {
      toast.error('Bitte wählen Sie eine Person aus');
      return;
    }

    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('user_id', selectedUser);
      params.append('hours', parseFloat(correctionHours).toString());
      params.append('date', correctionDate);
      params.append('note', correctionNote.trim());

      await api.post(`/overtime/correction?${params.toString()}`);
      
      toast.success('Korrektur erfolgreich gespeichert');
      
      setShowCorrectionDialog(false);
      setCorrectionHours('');
      setCorrectionNote('');
      setCorrectionDate(toIsoLocal(new Date()));
      fetchData(); // Refresh balance
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Korrektur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getBalanceColor = (balance) => {
    if (balance > 0) return 'text-emerald-600 dark:text-emerald-400';
    if (balance < 0) return 'text-red-600 dark:text-red-400';
    return 'text-muted-foreground';
  };

  const getBalanceIcon = (balance) => {
    if (balance > 0) return <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />;
    if (balance < 0) return <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />;
    return <Minus className="w-5 h-5 text-muted-foreground" />;
  };

  const formatPeriod = (start, end) => {
    return `${safeFormatDate(start, 'd. MMM')} - ${safeFormatDate(end, 'd. MMM yyyy')}`;
  };

  const getLedgerTypeLabel = (type) => {
    switch(type) {
      case 'accrual': return 'Ansammlung';
      case 'reduction': return 'Abbau';
      case 'payout': return 'Auszahlung';
      case 'correction': return 'Korrektur';
      case 'carryover': return 'Übertrag';
      default: return type;
    }
  };

  const getLedgerTypeColor = (type) => {
    switch(type) {
      case 'accrual': return 'bg-emerald-500/20 text-emerald-400';
      case 'reduction': return 'bg-blue-500/20 text-blue-400';
      case 'payout': return 'bg-amber-500/20 text-amber-400';
      case 'correction': return 'bg-purple-500/20 text-purple-400';
      case 'carryover': return 'bg-teal-500/20 text-teal-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (isLoading && selectedUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const availableBalance = overtimeBalance?.final_balance ?? timeAccount?.balance ?? 0;

  // Filter users by active status

  // Get selected user's name
  const selectedUserData = users.find((u) => u.id === selectedUser);
  const selectedUserName = selectedUserData 
    ? `${selectedUserData.first_name} ${selectedUserData.last_name}` 
    : '';

  return (
    <div data-testid="time-account-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/team-overview')}
            className="mb-2 -ml-2 text-muted-foreground hover:text-foreground"
            data-testid="back-to-team-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Team-Übersicht
          </Button>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Arbeitszeitkonten</h1>
          <p className="text-muted-foreground mt-1">Konten der Mitarbeitenden einsehen und verwalten</p>
        </div>

        <div className="flex items-center gap-4">
          <UserSelect users={users} value={selectedUser} onChange={(v) => setSelectedUser(v)} className="w-[200px]" />

          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px] bg-input border-border text-foreground">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* No user selected message */}
      {!selectedUser && (
        <div className="card-glass p-8 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Bitte eine Person auswählen</h3>
          <p className="text-sm text-muted-foreground">
            Wählen Sie oben eine Person aus, um deren Arbeitszeitkonto einzusehen.
          </p>
        </div>
      )}

      {/* Kennzahlenleiste */}
      {selectedUser && summary?.account && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="account-kpis">
          <div className="card-glass p-5">
            <p className="text-sm text-muted-foreground">Saldo</p>
            <p className={`text-3xl font-mono font-bold ${deltaColor(summary.account.balance)}`}>{fmtH(summary.account.balance)}</p>
          </div>
          <div className="card-glass p-5">
            <p className="text-sm text-muted-foreground">Davon verplant</p>
            <p className="text-3xl font-mono font-bold text-foreground">{fmtH(summary.account.committed_hours)}</p>
            <p className="text-xs text-muted-foreground mt-1">genehmigter und beantragter Abbau</p>
          </div>
          <div className="card-glass p-5">
            <p className="text-sm text-muted-foreground">Frei verfügbar</p>
            <p className="text-3xl font-mono font-bold text-foreground">{fmtH(summary.account.free_hours)}</p>
          </div>
        </div>
      )}

      {/* Main content only shown when user is selected */}
      {selectedUser && timeAccount && (
        <>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="overview" data-testid="tab-overview">Übersicht</TabsTrigger>
          <TabsTrigger value="weeks" data-testid="tab-weeks">Wochen</TabsTrigger>
        </TabsList>

        <TabsContent value="weeks" className="mt-6 space-y-4" data-testid="weeks-tab">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Switch id="only-with-entries" checked={onlyWithEntries} onCheckedChange={setOnlyWithEntries} data-testid="only-with-entries-switch" />
              <Label htmlFor="only-with-entries" className="text-sm text-muted-foreground">Nur Wochen mit Einträgen</Label>
            </div>
            {weeksData?.account_start && weeksData.account_start > `${selectedYear}-01-01` && (
              <p className="text-sm text-muted-foreground">Arbeitszeitkonto geführt ab {safeFormatDate(weeksData.account_start, 'd. MMMM yyyy')}.</p>
            )}
          </div>
          {weeksLoading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}
          {!weeksLoading && weeksData && visibleWeeks.length === 0 && (
            <p className="text-sm text-muted-foreground">Keine Wochen vorhanden.</p>
          )}
          <div className="space-y-2">
            {visibleWeeks.map((week) => {
              const badge = STATUS_BADGES[week.approval_status];
              const open = expandedWeek === week.week_start;
              return (
                <div key={week.week_start} className="card-glass overflow-hidden" data-testid={`week-row-${week.week_start}`}>
                  <button type="button" onClick={() => toggleWeek(week)} className="w-full text-left p-4 hover:bg-accent/30 transition-colors">
                    <div className="grid grid-cols-2 md:grid-cols-[120px_repeat(4,minmax(0,1fr))_auto] gap-3 items-center">
                      <div>
                        <p className="text-lg font-heading font-semibold text-foreground">KW {week.iso_week}</p>
                        <p className="text-xs text-muted-foreground">{safeFormatDate(week.week_start, 'd.M.')} - {safeFormatDate(week.week_end, 'd.M.yyyy')}</p>
                      </div>
                      <div className="text-sm"><span className="text-muted-foreground">Soll </span><span className="font-mono text-foreground">{fmtH(week.target_hours)}</span></div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Ist </span><span className="font-mono text-foreground">{fmtH(week.actual_hours)}</span>
                        {week.leave_hours > 0 && <p className="text-xs text-muted-foreground">Abwesenheit {fmtH(week.leave_hours)}</p>}
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Delta </span><span className={`font-mono font-medium ${deltaColor(week.delta_hours)}`}>{fmtSigned(week.delta_hours)}</span>
                        {week.ledger_hours !== 0 && <p className="text-xs text-muted-foreground">davon Korrekturen {fmtSigned(week.ledger_hours)}</p>}
                      </div>
                      <div className="text-sm"><span className="text-muted-foreground">Saldo Ende </span><span className={`font-mono font-medium ${deltaColor(week.closing_balance)}`}>{fmtH(week.closing_balance)}</span></div>
                      <div className="flex items-center gap-2 justify-end">
                        {badge && <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge[1]}`}>{badge[0]}</span>}
                        {!week.is_complete && <span className="text-xs text-muted-foreground">unvollständig</span>}
                        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </div>
                    </div>
                  </button>
                  {open && renderWeekDetail(week)}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="overview" className="mt-0">
      <div className="mt-6">
          {/* Main Balance Card - Yearly Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-glass p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-semibold text-foreground">Jahressaldo {selectedYear}</h2>
              <div className="flex items-center gap-3">
                {canCorrect && selectedUser && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCorrectionDialog(true)}
                    className="border-purple-500/30 text-purple-600 dark:text-purple-400 hover:bg-purple-500/10"
                    data-testid="open-correction-dialog-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Saldo anpassen
                  </Button>
                )}
                {getBalanceIcon(availableBalance)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Soll-Stunden</p>
                <p className="text-3xl font-heading font-bold text-foreground">
                  {formatHoursToHM(timeAccount?.target_hours || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {timeAccount?.weekly_hours || 40}h/Woche
                </p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Ist-Stunden</p>
                <p className="text-3xl font-heading font-bold text-indigo-600 dark:text-indigo-400">
                  {formatHoursToHM(timeAccount?.actual_hours || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Tatsächlich gearbeitet
                </p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Arbeitszeit-Saldo</p>
                <p className={`text-3xl font-heading font-bold ${getBalanceColor(timeAccount?.balance || 0)}`}>
                  {formatHoursToHM(timeAccount?.balance || 0, true)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Stand: gestern{timeAccount?.balance_as_of ? ` (${new Date(timeAccount.balance_as_of).toLocaleDateString('de-DE')})` : ''} · Vor Abbau/Auszahlung
                </p>
              </div>

              <div className="border-l border-border pl-8">
                <p className="text-sm text-muted-foreground mb-1">Verfügbares Zeitguthaben</p>
                <p className={`text-3xl font-heading font-bold ${getBalanceColor(availableBalance)}`}>
                  {formatHoursToHM(availableBalance || 0, true)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Nach Abbau/Auszahlung
                </p>
              </div>
            </div>

            {/* Ledger adjustments breakdown */}
            {overtimeBalance && (overtimeBalance.ledger_reduction !== 0 || overtimeBalance.ledger_payout !== 0 || overtimeBalance.ledger_correction !== 0 || overtimeBalance.ledger_carryover !== 0) && (
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground mb-3">Ledger-Anpassungen</p>
                <div className="flex flex-wrap gap-4">
                  {overtimeBalance.ledger_reduction !== 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 rounded-lg">
                      <ArrowDownRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm text-blue-600 dark:text-blue-400">Abbau: {overtimeBalance.ledger_reduction}h</span>
                    </div>
                  )}
                  {overtimeBalance.ledger_payout !== 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 rounded-lg">
                      <Banknote className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span className="text-sm text-amber-600 dark:text-amber-400">Auszahlung: {overtimeBalance.ledger_payout}h</span>
                    </div>
                  )}
                  {overtimeBalance.ledger_correction !== 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-purple-400">Korrektur: {overtimeBalance.ledger_correction > 0 ? '+' : ''}{overtimeBalance.ledger_correction}h</span>
                </div>
              )}
                  {overtimeBalance.ledger_carryover !== 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 rounded-lg">
                      <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-sm text-emerald-600 dark:text-emerald-400">Übertrag: {overtimeBalance.ledger_carryover > 0 ? '+' : ''}{overtimeBalance.ledger_carryover}h</span>
                    </div>
                  )}
            </div>
          </div>
        )}
      </motion.div>


      {/* Period Breakdown Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <PeriodCard title="Diese Woche" icon={CalendarDays} period={timeAccount?.weekly} color="indigo" formatPeriod={formatPeriod} getBalanceColor={getBalanceColor} getBalanceIcon={getBalanceIcon} />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <PeriodCard title="Dieser Monat" icon={CalendarRange} period={timeAccount?.monthly} color="amber" formatPeriod={formatPeriod} getBalanceColor={getBalanceColor} getBalanceIcon={getBalanceIcon} />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <PeriodCard title="Dieses Jahr" icon={CalendarCheck} period={timeAccount?.yearly} color="lime" formatPeriod={formatPeriod} getBalanceColor={getBalanceColor} getBalanceIcon={getBalanceIcon} />
        </motion.div>
      </div>

      {/* Vacation Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
          <VacationStatCard icon={Palmtree} label="Urlaubstage gesamt" value={`${timeAccount?.vacation_days_total || 30}`} color="emerald" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <VacationStatCard icon={Palmtree} label="Urlaub genommen" value={`${timeAccount?.vacation_days_used || 0}`} subtext="Vergangene Urlaubstage" color="amber" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
          <VacationStatCard icon={Calendar} label="Urlaub geplant" value={`${timeAccount?.vacation_days_planned || 0}`} subtext="Zukünftige genehmigte Tage" color="indigo" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <VacationStatCard icon={Palmtree} label="Urlaub verbleibend" value={`${timeAccount?.vacation_days_remaining || 0}`} color="lime" />
        </motion.div>
      </div>

      {/* Overtime Ledger History */}
      {overtimeLedger.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="card-glass p-6"
        >
          <h2 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Buchung Arbeitszeitkonto
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-muted-foreground border-b border-border">
                  <th className="pb-3 pr-4">Datum</th>
                  <th className="pb-3 pr-4">Typ</th>
                  <th className="pb-3 pr-4">Stunden</th>
                  <th className="pb-3 pr-4">Notiz</th>
                  <th className="pb-3">Erstellt von</th>
                </tr>
              </thead>
              <tbody>
                {overtimeLedger.slice(0, 10).map((entry) => (
                  <tr key={entry.id} className="border-b border-border">
                    <td className="py-3 pr-4 text-foreground">
                      {safeFormatDate(entry.date, 'd. MMM yyyy')}
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`px-2 py-1 text-xs rounded ${getLedgerTypeColor(entry.type)}`}>
                        {getLedgerTypeLabel(entry.type)}
                      </span>
                    </td>
                    <td className={`py-3 pr-4 font-mono font-semibold ${entry.hours >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                      {entry.hours >= 0 ? '+' : ''}{Number(entry.hours).toFixed(2)}h
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground text-sm max-w-xs truncate">
                      {entry.note || '-'}
                    </td>
                    <td className="py-3 text-muted-foreground text-sm">
                      {entry.created_by_name || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {overtimeLedger.length > 10 && (
            <p className="text-sm text-muted-foreground mt-4">
              Zeige 10 von {overtimeLedger.length} Einträgen
            </p>
          )}
        </motion.div>
      )}

      {/* Correction Dialog (Admin/Accounting only) */}
      <Dialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Arbeitszeitkonto-Saldo anpassen
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Korrektur für: {selectedUserName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <p className="text-sm text-purple-700 dark:text-purple-300">
                <strong>Hinweis:</strong> Positive Werte (z.B. +42,5) erhöhen den Saldo, negative Werte (z.B. -10) reduzieren ihn.
              </p>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Stunden *</label>
              <Input
                type="number"
                step="0.25"
                value={correctionHours}
                onChange={(e) => setCorrectionHours(e.target.value)}
                placeholder="z.B. 42.5 oder -10"
                className="bg-input border-border text-foreground"
                data-testid="correction-hours-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Positiv = Guthaben hinzufügen, Negativ = Saldo reduzieren
              </p>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Stichtag *</label>
              <Input
                type="date"
                value={correctionDate}
                onChange={(e) => setCorrectionDate(e.target.value)}
                className="bg-input border-border text-foreground"
                data-testid="correction-date-input"
              />
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Begründung *</label>
              <Input
                value={correctionNote}
                onChange={(e) => setCorrectionNote(e.target.value)}
                placeholder="z.B. Anfangsbestand Zeitguthaben bei CTIme-Einführung"
                className="bg-input border-border text-foreground"
                data-testid="correction-note-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Pflichtfeld - für die Nachvollziehbarkeit im Ledger
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowCorrectionDialog(false);
                setCorrectionHours('');
                setCorrectionNote('');
                setCorrectionDate(toIsoLocal(new Date()));
              }}
              className="text-muted-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmitCorrection}
              disabled={isSubmitting || !correctionHours || !correctionNote.trim()}
              className="bg-purple-500 hover:bg-purple-600 text-white"
              data-testid="submit-correction-btn"
            >
              {isSubmitting ? 'Wird gespeichert...' : 'Korrektur speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
        </TabsContent>
      </Tabs>
      </>
      )}
    </div>
  );
}
